import { useCallback, useEffect, useRef, useState } from "react";
import { useTheme } from "@mui/material";
import { IJumpMarkItem, IJumpMarksState, ISectionVisibility } from "src/components-mui/common/jump-marks/jump-marks.types";
import { findActiveJumpMarkId, getObserverRootMargin } from "src/components-mui/common/jump-marks/jump-marks.utils";
import { TResponsivePx } from "src/types/responsive";
import { getCurrentBreakpoint, subscribeToBreakpointChange } from "src/utils/mui/breakpoints/breakpoints";
import { resolveResponsivePx } from "src/utils/mui/responsive-px/responsive-px";

const FULLY_VISIBLE_RATIO = 0.99;
const OBSERVER_THRESHOLDS = [0, 1];

interface IUseActiveSectionTrackingParams {
    items: readonly IJumpMarkItem[];
    state: IJumpMarksState;
    scrollMarginPx: TResponsivePx;
}

interface ITrackedSection {
    id: string;
    element: HTMLElement;
}

export const useActiveSectionTracking = ({ items, state, scrollMarginPx }: IUseActiveSectionTrackingParams) => {
    const theme = useTheme();
    const [sectionIds] = useState(() => new Map<Element, string>());
    const [visibleSections] = useState(() => new Map<Element, ISectionVisibility>());
    const observerRef = useRef<IntersectionObserver | null>(null);

    const trackSection = useCallback(
        ({ id, element }: ITrackedSection) => {
            sectionIds.set(element, id);
            observerRef.current?.observe(element);

            return () => {
                sectionIds.delete(element);
                visibleSections.delete(element);
                observerRef.current?.unobserve(element);
            };
        },
        [sectionIds, visibleSections]
    );

    useEffect(() => {
        const handleIntersections = (entries: IntersectionObserverEntry[]) => {
            entries.forEach(({ target, isIntersecting, intersectionRatio, boundingClientRect }) => {
                if (isIntersecting) {
                    visibleSections.set(target, {
                        isFullyVisible: intersectionRatio >= FULLY_VISIBLE_RATIO,
                        hasHeight: boundingClientRect.height > 0,
                    });
                } else {
                    visibleSections.delete(target);
                }
            });

            const activeId = findActiveJumpMarkId({ items, sectionIds, visibleSections });

            if (activeId) {
                state.visibleId = activeId;
            }
        };

        const observeBelowStickyHeaders = () => {
            observerRef.current?.disconnect();
            visibleSections.clear();

            const currentScrollMarginPx = resolveResponsivePx({ valuesPx: scrollMarginPx, breakpoints: theme.breakpoints.keys })[
                getCurrentBreakpoint(theme)
            ];
            const observer = new IntersectionObserver(handleIntersections, {
                rootMargin: getObserverRootMargin(currentScrollMarginPx),
                threshold: OBSERVER_THRESHOLDS,
            });

            observerRef.current = observer;
            sectionIds.forEach((_, element) => observer.observe(element));
        };

        observeBelowStickyHeaders();
        const unsubscribeFromBreakpointChange = subscribeToBreakpointChange(theme, observeBelowStickyHeaders);

        return () => {
            unsubscribeFromBreakpointChange();
            observerRef.current?.disconnect();
            observerRef.current = null;
        };
    }, [items, scrollMarginPx, sectionIds, state, theme, visibleSections]);

    return trackSection;
};
